// src/components/ui.js — V18 primitives shared by every page.

import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { color, font } from '../theme/tokens';

/* ---------- seal (印章) ---------- */
export const Seal = styled.div`
    width: ${({ $size = 44 }) => $size}px;
    height: ${({ $size = 44 }) => $size}px;
    flex-shrink: 0;
    box-sizing: border-box;
    border: 1px solid ${color.blood};
    background: ${({ $outline }) => ($outline ? 'transparent' : color.blood)};
    color: ${({ $outline }) => ($outline ? color.bloodHi : color.onBlood)};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${font.display};
    font-weight: 800;
    font-size: ${({ $size = 44 }) => Math.round($size * 0.5)}px;
    line-height: 1;
`;

/* ---------- buttons ---------- */
const buttonBase = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-sizing: border-box;
    min-height: 48px;
    padding: 0 28px;
    border: 1px solid transparent;
    border-radius: 0;
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.3em;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, opacity 0.25s ease;
    &:disabled, &[aria-disabled='true'] { opacity: 0.45; cursor: not-allowed; }
`;

const primary = css`
    ${buttonBase};
    background-color: ${color.blood};
    color: ${color.onBlood};
    font-weight: 800;
    &:hover:not(:disabled) { background-color: ${color.bloodHi}; }
`;

const ghost = css`
    ${buttonBase};
    background-color: transparent;
    border-color: ${color.line2};
    color: ${color.text2};
    letter-spacing: 0.25em;
    font-size: 14px;
    &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.text}; }
`;

export const PrimaryButton = styled.button`${primary}; width: ${({ $block }) => ($block ? '100%' : 'auto')};`;
export const GhostButton = styled.button`${ghost}; width: ${({ $block }) => ($block ? '100%' : 'auto')};`;
export const PrimaryLink = styled(Link)`${primary};`;
export const GhostLink = styled(Link)`${ghost};`;

export const TextLink = styled(Link)`
    color: ${color.bloodHi};
    text-decoration: none;
    border-bottom: 1px solid rgba(224, 70, 79, 0.45);
    padding-bottom: 1px;
    &:hover { color: ${color.textBright}; border-color: ${color.textBright}; }
`;

/* ---------- tags ---------- */
export const Tag = styled.span`
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 10px;
    font-size: 12px;
    letter-spacing: 0.2em;
    white-space: nowrap;
    ${({ $solid }) => ($solid
        ? css`background: ${color.blood}; color: ${color.onBlood};`
        : css`border: 1px solid rgba(224, 70, 79, 0.6); color: ${color.bloodHi};`)}
`;

export const VersionTag = styled.span`
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 10px;
    border: 1px solid rgba(224, 70, 79, 0.5);
    font-family: ${font.mono};
    font-size: 13px;
    color: ${color.bloodHi};
    letter-spacing: 0.06em;
`;

/* ---------- card with blood corner ticks (結界枠) ---------- */
const CardShell = styled.div`
    position: relative;
    box-sizing: border-box;
    padding: ${({ $pad = '32px 36px' }) => $pad};
    background: ${({ $bg }) => $bg || color.surface};
    border: 1px solid ${color.line};
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }
    @media (max-width: 640px) { padding: 22px 18px; }
`;
export const CornerCard = ({ children, pad, bg, className, style, as }) => (
    <CardShell $pad={pad} $bg={bg} className={className} style={style} as={as}>{children}</CardShell>
);

/* ---------- section heading ---------- */
const SectionHead = styled.div`
    display: flex;
    align-items: baseline;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${color.line};
    margin-bottom: 8px;
    h2 {
        margin: 0;
        font-family: ${font.display};
        font-weight: 800;
        font-size: 26px;
        letter-spacing: 0.2em;
        color: ${color.text};
    }
    span { font-size: 14px; color: ${color.text3}; letter-spacing: 0.12em; }
`;
export const SectionTitle = ({ jp, cn, children }) => (
    <SectionHead>
        <h2>{jp}</h2>
        {cn && <span>{cn}</span>}
        {children}
    </SectionHead>
);

/* ---------- chapter marker ---------- */
const ChapterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    color: ${color.text3};
    font-family: ${font.display};
    font-size: 13px;
    letter-spacing: 0.5em;
    &::before, &::after { content: ''; width: 56px; height: 1px; }
    &::before { background: linear-gradient(90deg, transparent, rgba(200, 50, 62, 0.6)); }
    &::after { background: linear-gradient(90deg, rgba(200, 50, 62, 0.6), transparent); }
`;
export const Chapter = ({ children, className }) => <ChapterRow className={className}>{children}</ChapterRow>;

/* ---------- form fields ---------- */
export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    label { font-size: 13px; letter-spacing: 0.2em; color: ${color.text2}; }
`;

export const Input = styled.input`
    box-sizing: border-box;
    width: 100%;
    height: 48px;
    padding: 0 16px;
    background: rgba(7, 2, 5, 0.7);
    border: 1px solid ${({ $invalid }) => ($invalid ? color.bloodHi : color.line2)};
    border-radius: 0;
    color: ${color.text};
    font-family: ${font.body};
    font-size: 15px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    &::placeholder { color: ${color.text3}; }
    &:focus { outline: none; border-color: ${color.bloodHi}; box-shadow: 0 0 0 3px rgba(224, 70, 79, 0.18); }
    &:disabled { opacity: 0.6; }
`;

export const TextArea = styled(Input).attrs({ as: 'textarea' })`
    height: auto;
    min-height: 160px;
    padding: 14px 16px;
    line-height: 1.8;
    resize: vertical;
`;

export const ErrorText = styled.p`
    margin: 0;
    min-height: 1.2em;
    font-size: 13px;
    color: ${color.bloodHi};
`;

export const Muted = styled.p`
    margin: 0;
    padding: 48px 0;
    text-align: center;
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.3em;
    color: ${color.text3};
`;
